import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import {
  Button,
  Popover,
  Stack,
  DisplayText,
  ActionList
} from '@superops/ui-design-system';
import CSVImportButton from '../../Pages/MigrateDataPage/CsvImport';
import { IMPORT_CONFIG } from './config';
import { useProduct, COMMON_CONSTANTS } from '@nile/components';
import { useUserMSPCheckHook } from '@nile/components/src/Utils/helpers';

interface ImportButtonProps {
  module: keyof typeof IMPORT_CONFIG;
  onImportComplete?: () => void;
  onIntegrationClick?: (route: string) => void;
  onScriptClick?: () => void;
}

const ImportButton: React.FC<ImportButtonProps> = ({
  module,
  onImportComplete,
  onIntegrationClick,
  onScriptClick
}) => {
  const intl = useIntl();
  const [popoverActive, setPopoverActive] = useState(false);
  const isDromoActiveRef = React.useRef<Record<string, boolean>>({});
  const isMsp = useUserMSPCheckHook();

  const lastCsvClickRef = React.useRef<number>(0);
  const debounceCsvClick = (optionKey: string) => (
    e: React.MouseEvent,
    delay = 3000
  ) => {
    const now = Date.now();
    if (now - lastCsvClickRef.current < delay) {
      return;
    }

    if (isDromoActiveRef.current[optionKey]) {
      return;
    }

    lastCsvClickRef.current = now;
    e.stopPropagation();
    e.preventDefault();
    const button =
      (e.currentTarget as HTMLElement).querySelector('button') ||
      (e.currentTarget as HTMLElement).querySelector('[data-type="button"]');

    if (button) {
      button.click();
    }
  };

  const handleDromoStateChange = (optionKey: string, isActive: boolean) => {
    isDromoActiveRef.current[optionKey] = isActive;
  };

  const moduleConfig = IMPORT_CONFIG[module];

  if (!moduleConfig) {
    console.warn(`No configuration found for module: ${module}`);
    return null;
  }

  const togglePopover = () => setPopoverActive(!popoverActive);

  const handleIntegrationClick = (route: string) => {
    if (onIntegrationClick) {
      onIntegrationClick(route);
    }
    togglePopover();
  };

  const handleScriptClick = () => {
    if (onScriptClick) {
      onScriptClick();
    }
    togglePopover();
  };

  const handleImportComplete = () => {
    togglePopover();
    if (onImportComplete) {
      onImportComplete();
    }
  };

  const buildActionSections = () => {
    const sections: Array<{ title?: React.ReactNode; items: any[] }> = [];
    const addSection = (title?: React.ReactNode) => {
      const section = { title, items: [] as any[] };
      sections.push(section);
      return section.items;
    };

    if ('csvOptions' in moduleConfig && moduleConfig.csvOptions) {
      const csvItems = addSection(
        <span>
          <FormattedMessage id="common.import.viacsv" />
        </span>
      );
      moduleConfig.csvOptions.forEach((option) => {
        csvItems.push({
          // prefix: (
          //   <span onClick={debounceCsvClick(option.key)}>
          //     <SVGIcon name={option.icon} size="sm" />
          //   </span>
          // ),
          content: (
            <div
              className="dromoBtnTrans tw-px-2 tw-py-2"
              onClick={debounceCsvClick(option.key)}
            >
              <div className="flex center" style={{ pointerEvents: 'none' }}>
                <span
                  className="tw-mr-2"
                  onClick={debounceCsvClick(option.key)}
                >
                  <SVGIcon name={option.icon} size="md" />
                </span>
                <CSVImportButton
                  type={option.key}
                  onImportComplete={handleImportComplete}
                  onDromoStateChange={(isActive: boolean) =>
                    handleDromoStateChange(option.key, isActive)
                  }
                  uiType="dropdown"
                >
                  {option.label}
                </CSVImportButton>
              </div>
            </div>
          ),
          renderAsCustom: true
        });
      });
    }

    if (
      'integrationOptions' in moduleConfig &&
      moduleConfig.integrationOptions
    ) {
      const integrationItems = addSection(
        <span>
          {' '}
          <FormattedMessage id="common.import.viaintegrations" />
        </span>
      );
      moduleConfig.integrationOptions.forEach((option) => {
        integrationItems.push({
          prefix: <SVGIcon name={option.icon} size="sm" />,
          content: option.label,
          onAction: () => handleIntegrationClick(option.route)
        });
      });
    }

    if ('scriptOption' in moduleConfig && moduleConfig.scriptOption) {
      const scriptItems = addSection(
        <span>
          <FormattedMessage id="common.import.viascript" />
        </span>
      );
      scriptItems.push({
        prefix: <SVGIcon name={moduleConfig.scriptOption.icon} size="sm" />,
        content: moduleConfig.scriptOption.label,
        onAction: handleScriptClick
      });
    }

    return sections;
  };

  const activator = (
    <Button onClick={togglePopover} icon={<SVGIcon name="Import" size="md" />}>
      <FormattedMessage id="common.import" />
      <span className="tw-ml-1">
        <SVGIcon name="ArrowDown" size="sm" />
      </span>
    </Button>
  );

  if (!isMsp) {
    return null;
  }

  return (
    <Popover
      active={popoverActive}
      activator={activator}
      onClose={togglePopover}
      preferredAlignment="left"
      customClassName="w260 consolidated-import-wrapper"
    >
      <div>
        <ActionList actionRole="menuitem" sections={buildActionSections()} />
      </div>
    </Popover>
  );
};

export default ImportButton;
